// Extracted body of the Receipt Review modal. Lives in its own file
// so `ManufacturingReceiptExtractionModal.tsx` stays under the
// file-size-hygiene threshold. Renders the grey "what OCR read"
// reference panel, the per-line extracted rows (with the searchable
// material picker + inline create-new), and the collapsed
// already-posted list.
//
// Pure presentational + delegating -- all post / draft mutation
// happens in the modal; this body only receives drafts and emits
// changes back up.
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type {
  MfgInventoryChangelogRow,
  MfgReceiptExtractedShape,
  MfgReceiptLineMatchSuggestion,
} from "@/lib/manufacturing/types";
import {
  draftFromExtractedLine,
  ManufacturingReceiptExtractedLineRow,
  type ExtractedLineDraft,
} from "./ManufacturingReceiptExtractedLineRow";

type Props = {
  extracted: MfgReceiptExtractedShape;
  suggestions: Array<MfgReceiptLineMatchSuggestion | null>;
  drafts: ExtractedLineDraft[];
  materials: MfgMaterial[];
  workingIdx: number | null;
  onDraftChange: (idx: number, next: ExtractedLineDraft) => void;
  onPost: (idx: number) => void;
  onMaterialCreated: (material: MfgMaterial) => void;
  alreadyPosted: MfgInventoryChangelogRow[];
};

export function ManufacturingReceiptExtractionBody({
  extracted,
  suggestions,
  drafts,
  materials,
  workingIdx,
  onDraftChange,
  onPost,
  onMaterialCreated,
  alreadyPosted,
}: Props) {
  return (
    <div className="mt-3 space-y-3">
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-zinc-700 dark:bg-zinc-800/40">
        <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-500">
          What OCR read from the file (reference)
        </div>
        <div className="grid grid-cols-3 gap-2 text-[11px]">
          <Field label="date" value={extracted.date ?? "—"} />
          <Field
            label="total"
            value={extracted.total != null ? `$${extracted.total.toFixed(2)}` : "—"}
          />
          <Field label="shipping" value={`$${extracted.shipping.toFixed(2)}`} />
          <Field label="tax" value={`$${extracted.tax.toFixed(2)}`} />
        </div>
      </div>

      {extracted.line_items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400">
          OCR didn't extract any line items. Use Re-extract or upload a clearer
          image and try again.
        </div>
      ) : (
        <ul className="space-y-2">
          {extracted.line_items.map((line, idx) => (
            <ManufacturingReceiptExtractedLineRow
              key={idx}
              line={line}
              draft={drafts[idx] ?? draftFromExtractedLine(line, suggestions[idx] ?? null)}
              suggestion={suggestions[idx] ?? null}
              materials={materials}
              working={workingIdx === idx}
              onDraftChange={(next) => onDraftChange(idx, next)}
              onPost={() => onPost(idx)}
              onMaterialCreated={onMaterialCreated}
            />
          ))}
        </ul>
      )}

      {alreadyPosted.length > 0 ? (
        <details className="rounded-lg border border-gray-200 bg-white p-2 text-xs dark:border-zinc-700 dark:bg-zinc-900">
          <summary className="cursor-pointer text-[11px] font-medium text-gray-700 dark:text-zinc-300">
            Already posted from this receipt ({alreadyPosted.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {alreadyPosted.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2 py-1 dark:border-zinc-800"
              >
                <span className="truncate text-gray-800 dark:text-zinc-200">
                  {e.material_name ?? e.material_id?.slice(0, 8) ?? "material"}
                </span>
                <span className="tabular-nums text-gray-600 dark:text-zinc-400">
                  {e.quantity} × ${e.unit_cost.toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-zinc-500">
        {label}
      </div>
      <div className="text-xs text-gray-900 dark:text-zinc-100">{value}</div>
    </div>
  );
}
